import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CallBadge } from './CallBadge';
import { reasoningTooltip } from './ReasoningBadge';
import {
  askKey, pageCropAsks, runPageCrops, stepHardwarePrompt, stepShots, STAGES, useHardwareCrops,
  type Candidate, type Stage,
} from './hardwareCropsRun';
import { useListingCropAsks } from './listingCropsRun';
import { useAssemblySteps } from './categoryBands';
import { StepPromptNav } from './StepPromptNav';
import { BoxOverlay } from './BoxOverlay';
import { rowTone } from './ListingCropCell';
import { renderPartCrops } from './partCrop';
import type { StepBand } from './categoryBands';
import type { StepShot } from './hardwareCropsRun';
import type { PartItem } from './partsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// One empty list, so a step with no pictures does not get a new array on every render.
const NO_SHOTS: StepShot[] = [];
const NO_FOUND: Candidate[] = [];
// What the panel is holding: one step's pictures, and WHICH STEP THEY ARE OF. The pair and
// not the list alone, because rendering them is a pass over the PDF and the arrows move
// faster than that — see the state below.
type Held = { at: string; srcs: StepShot[] };
const NOTHING_HELD: Held = { at: '', srcs: NO_SHOTS };

// WHERE EVERY PART IS DRAWN IN THE MANUAL: the two questions, and what every step answered.
//
// TWO STEPPERS AND ONE BADGE. A step is read by two calls — what is drawn, then which part each
// one is — and they are one question in two halves rather than two features: the second reads
// the first's answer and neither is worth looking at without the other. So ← → walks the steps
// and ↑ ↓ walks the pair, and the words and the pictures under them are always one real call.
//
// See hardwareCropsRun for why it is split that way at all.
export function HardwareCropsInfo({ docId, file, items, categories, disabled, onStop }: {
  docId: string;
  file: Blob;
  // Every part's category, the database's first — see usePartCategories.
  categories: string[];
  // The parts this describes, handed in rather than read live: a re-run empties the store's
  // list before it refills it, and a prompt built from nothing is a preview of no call.
  items: PartItem[];
  // Something else is in flight — the whole press — or the phase is locked. See CallRun.
  disabled: boolean;
  // STOP, while this badge's run is out and was started on its own (see CallRun.onStop).
  onStop?: () => void;
}) {
  const { t } = useLanguage();
  const { notes, busy, read, candidates } = useHardwareCrops(docId);
  // THE STEPS THIS CALL IS MADE FOR — the same list the analysis and the holes walk, from the
  // same place, so the panel's "Step 1 · 1/3" means what it means everywhere else.
  const { steps } = useAssemblySteps(docId);
  const [stepAt, setStepAt] = useState(0);
  const at = Math.min(stepAt, Math.max(0, steps.length - 1));
  const step = steps[at];
  // AND WHICH OF THE TWO CALLS. The find call carries no part at all, so the other axis is the
  // stage: the same step, asked twice.
  const eligible = useMemo(() => pageCropAsks(items), [items]);
  // Which of them the photograph call asks about too — for the colours, which are the row's.
  const listing = useListingCropAsks(items, categories);
  const [stageAt, setStageAt] = useState(0);
  const nth = Math.min(stageAt, STAGES.length - 1);
  const stage: Stage = STAGES[nth];
  // WHAT THE FIND CALL SAW IN THIS STEP — the match call's whole input, and what the panel
  // draws under either stage.
  const found = (step && candidates[step.category]) || NO_FOUND;

  // THIS STEP'S PICTURES, rendered when somebody opens the panel and not before: it is a pass
  // over the PDF, and the panel is the one place that needs them without a call being made.
  // One step at a time — the arrows are how the others are asked for.
  //
  // THE PROMISE IS WHAT IS CACHED, not the pictures. Pressing → twice quickly used to start
  // the same render twice; and every reader of these has to be able to WAIT for them, which a
  // piece of state that is filled a moment later cannot be waited on.
  const made = useRef(new Map<string, Promise<StepShot[]>>());
  useEffect(() => { made.current = new Map(); }, [file]);
  const srcsOf = useCallback((one: StepBand): Promise<StepShot[]> => {
    const known = made.current.get(one.category);
    if (known) return known;
    const job = stepShots(file, [one]).then((all) => all.get(one.category) ?? []);
    made.current.set(one.category, job);
    return job;
  }, [file]);

  // AND THE ONES ON SCREEN, which is the pair rather than the list: the arrows move between
  // steps faster than a page rasterises, so a bare list would be the PREVIOUS step's pictures
  // under this step's heading until the new ones land — which is exactly what it was, and what
  // made the picture look wrong and disagree with itself from one look to the next.
  const [held, setHeld] = useState<Held>(NOTHING_HELD);
  useEffect(() => {
    if (!step) return undefined;
    let gone = false;
    void srcsOf(step).then((srcs) => {
      if (!gone) setHeld({ at: step.category, srcs });
    });
    return () => { gone = true; };
  }, [srcsOf, step]);
  // Nothing at all until they are THIS step's. An empty strip says "still rendering", where
  // another step's drawing says something false.
  const shots = step && held.at === step.category ? held.srcs : NO_SHOTS;

  // The drawings the call carries, rendered once per list — the same pass the photograph call
  // makes, and the same reason: the prompt numbers them per part, so a preview built without
  // them would name fewer pictures than the press sends.
  const drawings = useRef<{ items: PartItem[]; done: Promise<(string | null)[]> } | null>(null);
  //
  // IT ASKS FOR THE STEP'S PICTURES ITSELF rather than reading them off the state above. The
  // panel refetches when `revision` changes, with the `load` it holds AT THAT MOMENT — and a
  // load built from state that is one render behind describes the step before this one. The
  // count is in the words, so that is not a stale picture beside the right text: it is the
  // wrong question. Awaiting them here makes the prompt and the pictures the same step's by
  // construction, whatever order the renders land in.
  const load = useCallback(async () => {
    if (drawings.current?.items !== items) {
      drawings.current = { items, done: renderPartCrops(file, items) };
    }
    const mine = step ? await srcsOf(step) : NO_SHOTS;
    const drawn = await drawings.current.done;
    return stepHardwarePrompt(stage, items, eligible, drawn, mine, found);
  }, [file, items, eligible, stage, step, found, srcsOf]);

  // WHAT THIS CALL ANSWERED — this stage, in this step. During a run, only once THIS run has
  // made it: before that the note is the last run's.
  const key = step ? askKey(step.category, stage) : '';
  const note = key && (!busy || read.includes(key)) ? notes[key] : undefined;
  const answer = useMemo(() => {
    if (!note) return undefined;
    // THE RECTANGLES THEMSELVES, not a re-reading of the raw text. Both stages are about the
    // same set of boxes — the first drew them, the second named them — so the panel draws the
    // set and lets the label say which stage it is looking at.
    //
    // Under FIND that label is the number painted on the picture the second call reads, and
    // the letter the manual points at it with. Under MATCH it is the part, or a dash where the
    // call said it is none of them, which is the answer somebody opens this panel for.
    const boxes = found.map((one) => ({
      shot: one.shot,
      box: one.bbox,
      label: stage === 'find'
        ? `${one.n}${one.letter ? ` · ${one.letter}` : ''}`
        : one.part == null
          ? `${one.n} · —`
          : `${one.n} · ${items[one.part]?.nameEn || items[one.part]?.name
            || t('notice.parts.piece')}`,
      // Its own colour once it has a part, the one the row draws that part's boxes in; the
      // finder's are all one colour, because at that point they are all the same kind of
      // claim.
      tone: stage === 'match' && one.part != null
        ? rowTone(one.part, listing, eligible) : undefined,
    }));
    return {
      label: t(stage === 'find' ? 'notice.parts.hardwareFindAnswer'
        : 'notice.parts.hardwareAnswer'),
      content: (
        <div className="space-y-4">
          {reasoningTooltip(note.reasoning, note.raw, t, null, note.unreadable)}
          {/* THE STEP AS IT WAS SENT, with the rectangles on it. One picture per band, in the
              order they went — a box says which of them it is in, and drawing it on the wrong
              one would be drawing it in the wrong place. */}
          {boxes.length > 0 && shots.map((band, n) => (
            <div key={band.src} className="flex justify-center">
              <span className="relative inline-block">
                <img src={band.src} alt="" className="block max-h-[36rem] max-w-full rounded-lg bg-white" />
                {boxes.filter((b) => b.shot === n).map((b, k) => (
                  <BoxOverlay key={k} box={b.box} label={b.label} tone={b.tone} veil={false} />
                ))}
              </span>
            </div>
          ))}
        </div>
      ),
    };
  }, [note, shots, found, stage, items, eligible, listing, t]);

  // DRAWN EVEN WITH NOTHING TO ASK ABOUT, greyed out: the phase shows its whole sequence.
  const nothingToAsk = !eligible.length || !steps.length;

  return (
    <CallBadge
      title={t('notice.parts.badgeHardwareCrops')}
      named
      run={{
        // EVERY PAGE, WHATEVER TAB YOU ARE ON. There is no scoping here: the page is billed
        // whole, so asking about one part costs what asking about all of them does — the
        // argument the photograph call makes for describing every part, one level up.
        onRun: () => void renderPartCrops(file, items)
          .then((drawn) => runPageCrops(docId, file, steps, items, drawn)),
        busy,
        disabled: disabled || nothingToAsk,
        onStop,
      }}
      promptLabel={t(stage === 'find' ? 'notice.parts.hardwareFindPrompt'
        : 'notice.parts.hardwarePrompt')}
      load={load}
      revision={`step:${step?.category ?? ''}:${stage}:${found.length}`}
      // TWO STEPPERS, ONE CALL. The step takes ← →, the stage ↑ ↓: a step is read by two
      // calls, and the words under this heading are one of them rather than a summary of
      // both.
      promptNav={(
        <>
          <StepPromptNav
            steps={steps.map((one) => one.num)}
            at={at}
            onGo={setStepAt}
          />
          <StepPromptNav
            steps={STAGES.map((_, i) => i + 1)}
            at={nth}
            onGo={setStageAt}
            labelOf={(i) => t(STAGES[i] === 'find' ? 'notice.parts.hardwareFind'
              : 'notice.parts.hardwareMatch')}
            vertical
          />
        </>
      )}
      answer={answer}
    />
  );
}
